import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { toast } from "sonner";
import { Camera, CheckCircle, X, Warning } from "@phosphor-icons/react";

/**
 * SelfieCapture — fullscreen identity-verification selfie capture.
 *
 * Locked to the FRONT camera. NO gallery / file picker / multi-source upload.
 * Used during registration and as a member-dashboard guard until a selfie exists.
 *
 * Props:
 *   onCaptured(url) — called after successful save
 *   onSkip          — optional, only useful for soft-prompts; unset for hard gates
 *   subtitle        — override default subtitle copy
 */
export default function SelfieCapture({ onCaptured, onSkip, subtitle }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [stage, setStage] = useState("idle"); // idle | camera | preview | submitting
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => stopStream(), []);

  const startCamera = async () => {
    setError(null);
    stopStream();
    try {
      // Force the user-facing (front) camera; if unavailable, fail gracefully.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "user" }, width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStage("camera");
    } catch (err) {
      setError(err?.name === "NotAllowedError"
        ? "Camera access was denied. Please enable camera permission in your browser settings, then try again."
        : "Could not start the front camera. A working selfie camera is required to continue.");
      setStage("idle");
    }
  };

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const size = Math.min(v.videoWidth, v.videoHeight);
    c.width = 720; c.height = 720;
    const ctx = c.getContext("2d");
    const sx = (v.videoWidth - size) / 2;
    const sy = (v.videoHeight - size) / 2;
    // Mirror so the saved photo matches what the user just saw on screen
    ctx.translate(c.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(v, sx, sy, size, size, 0, 0, c.width, c.height);
    const dataUrl = c.toDataURL("image/jpeg", 0.86);
    setPreview(dataUrl);
    stopStream();
    setStage("preview");
  };

  const retake = () => {
    setPreview(null);
    startCamera();
  };

  const save = async () => {
    if (!preview) return;
    setStage("submitting");
    try {
      await api.post("/members/me/identity-photo", { photo_url: preview, captured_via: "camera" });
      toast.success("Selfie saved. Your identity is verified.");
      onCaptured?.(preview);
    } catch (err) {
      toast.error(formatApiError(err));
      setStage("preview");
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[#050505] grain overflow-y-auto" data-testid="selfie-modal">
      <div className="min-h-full flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-gold-gradient text-black mb-3">
              <Camera size={26} weight="duotone" />
            </div>
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Identity Verification Selfie</p>
            <h2 className="font-display text-3xl text-white mt-2">Position your face within the circle</h2>
            <p className="text-white/55 text-sm mt-3 max-w-sm mx-auto">
              {subtitle || "This selfie is shown to merchants when they scan your QR — proof that the privilege is truly yours."}
            </p>
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]/80 mt-3 flex items-center justify-center gap-2">
              <Warning size={12} weight="bold" /> Selfie only · gallery uploads are disabled
            </p>
          </div>

          {error && (
            <div className="border border-red-500/40 bg-red-500/10 text-red-200 p-3 rounded text-sm mb-4" data-testid="selfie-error">
              {error}
            </div>
          )}

          {stage === "idle" && (
            <div className="tsc-card p-8 text-center">
              <button onClick={startCamera} className="tsc-btn-gold inline-flex items-center gap-2" data-testid="selfie-start">
                <Camera size={18} weight="duotone" /> Open front camera
              </button>
              <p className="text-white/40 text-[11px] mt-4">Your browser will request camera permission.</p>
              {onSkip && (
                <button onClick={onSkip} className="text-white/45 hover:text-white text-[10px] tracking-luxe uppercase mt-5" data-testid="selfie-skip">
                  Skip for now
                </button>
              )}
            </div>
          )}

          {stage === "camera" && (
            <div data-testid="selfie-camera-stage">
              <div className="relative aspect-square bg-black rounded-md overflow-hidden">
                <video
                  ref={videoRef}
                  playsInline muted
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ transform: "scaleX(-1)" }}
                />
                {/* Circular face guide overlay */}
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div
                    className="aspect-square w-[78%] rounded-full border-2 border-[#D4AF37]"
                    style={{ boxShadow: "0 0 0 1000px rgba(0,0,0,.55)" }}
                  />
                </div>
                <div className="pointer-events-none absolute top-3 left-1/2 -translate-x-1/2 text-[10px] tracking-luxe uppercase text-[#D4AF37] bg-black/50 px-3 py-1 rounded-full">
                  Front camera
                </div>
              </div>
              <button
                type="button" onClick={capture}
                className="tsc-btn-gold w-full mt-5 flex items-center justify-center gap-2"
                data-testid="selfie-capture"
              >
                <Camera size={20} weight="bold" /> Capture selfie
              </button>
            </div>
          )}

          {(stage === "preview" || stage === "submitting") && preview && (
            <div data-testid="selfie-preview-stage">
              <div className="relative aspect-square bg-black rounded-md overflow-hidden">
                <img src={preview} alt="Selfie preview" className="absolute inset-0 w-full h-full object-cover" data-testid="selfie-preview-img" />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="aspect-square w-[78%] rounded-full border border-[#D4AF37]/30" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-5">
                <button type="button" onClick={retake} disabled={stage === "submitting"} className="tsc-btn-outline flex items-center justify-center gap-2" data-testid="selfie-retake">
                  <X size={16} /> Retake
                </button>
                <button type="button" onClick={save} disabled={stage === "submitting"} className="tsc-btn-gold flex items-center justify-center gap-2" data-testid="selfie-save">
                  <CheckCircle size={18} weight="bold" /> {stage === "submitting" ? "Saving…" : "Use selfie"}
                </button>
              </div>
            </div>
          )}

          <canvas ref={canvasRef} className="hidden" />
        </div>
      </div>
    </div>
  );
}
